import React, { useEffect, useState } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import { effectiveModeTokens, type ThemeModeTokens } from '../../store/types';
import { exportTheme, parseThemeJson } from '../../io/theme-io';
import { pickFile } from '../../io/download';

interface Props { themeId: string; onClose: () => void }

/** Minimal manual editor: edit a theme's color + radius + spacing string
 *  values and typography font/size. Each change dispatches theme.update.
 *  Deliberately omits the optional shadow/motion/components token groups —
 *  those are agent/import-authored only. */
export function ThemeEditorModal({ themeId, onClose }: Props) {
  const theme = useCanvasStore((s) => (s.doc.themes ?? []).find((t) => t.id === themeId));
  const dispatch = useCanvasStore((s) => s.dispatch);
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // Escape closes, matching ThemeLibraryModal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!theme) return null;

  // The token set being edited: light = top-level fields; dark = theme.dark
  // (mergeTheme seeds it from light on the first dark edit).
  const active: ThemeModeTokens = effectiveModeTokens(theme, mode === 'dark');

  // Patch the active mode. Light patches the top-level groups; dark patches theme.dark.
  const patchMode = (group: keyof ThemeModeTokens, value: unknown) => {
    if (mode === 'dark') dispatch({ t: 'theme.update', id: themeId, patch: { dark: { [group]: value } } as never });
    else dispatch({ t: 'theme.update', id: themeId, patch: { [group]: value } as never });
  };
  const setColor = (k: string, v: string) => patchMode('colors', { ...active.colors, [k]: v });
  const setRounded = (k: string, v: string) => patchMode('rounded', { ...active.rounded, [k]: v });
  const setSpacing = (k: string, v: string) => patchMode('spacing', { ...active.spacing, [k]: v });
  const setType = (k: string, field: 'fontFamily' | 'fontSize', v: string) =>
    patchMode('typography', { ...active.typography, [k]: { ...active.typography[k], [field]: v } });

  const onImport = async () => {
    const file = await pickFile('.json');
    if (!file) return;
    try { dispatch({ t: 'theme.create', theme: parseThemeJson(await file.text()) }); }
    catch (err) { window.alert(`Import failed: ${err instanceof Error ? err.message : String(err)}`); }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 'min(560px,92vw)', maxHeight: '80vh', overflow: 'auto', background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, fontSize: 12 }}
      >
        <div className="flex items-center justify-between mb-3 gap-2">
          <strong className="truncate">Edit: {theme.name}</strong>
          <div className="flex items-center gap-1">
            {(['light', 'dark'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className="px-2 py-0.5 rounded text-[11px]"
                style={{
                  background: mode === m ? 'var(--color-menu-hover)' : 'transparent',
                  border: '1px solid var(--color-border)',
                  fontWeight: mode === m ? 700 : 400,
                }}
              >
                {m}
              </button>
            ))}
            <button onClick={() => exportTheme(theme)} title="Export JSON" className="px-1.5 py-0.5 rounded text-[11px]" style={{ border: '1px solid var(--color-border)' }}>⬇</button>
            <button onClick={onImport} title="Import JSON" className="px-1.5 py-0.5 rounded text-[11px]" style={{ border: '1px solid var(--color-border)' }}>⬆</button>
            <button onClick={onClose} className="px-2 py-1 rounded hover:bg-[var(--color-menu-hover)]">✕</button>
          </div>
        </div>

        <Group title="colors">
          {Object.entries(active.colors).map(([k, v]) => (
            <Row key={k} label={k}>
              <input type="color" value={normalizeColor(v)} onChange={(e) => setColor(k, e.target.value)} />
              <input value={v} onChange={(e) => setColor(k, e.target.value)} style={inputStyle} />
            </Row>
          ))}
        </Group>

        <Group title="typography">
          {Object.entries(active.typography).map(([k, ty]) => (
            <Row key={k} label={k}>
              <input placeholder="font-family" value={ty.fontFamily ?? ''} onChange={(e) => setType(k, 'fontFamily', e.target.value)} style={inputStyle} />
              <input placeholder="font-size" value={ty.fontSize ?? ''} onChange={(e) => setType(k, 'fontSize', e.target.value)} style={{ ...inputStyle, width: 80 }} />
            </Row>
          ))}
        </Group>

        <Group title="rounded">
          {Object.entries(active.rounded).map(([k, v]) => (
            <Row key={k} label={k}><input value={v} onChange={(e) => setRounded(k, e.target.value)} style={inputStyle} /></Row>
          ))}
        </Group>

        <Group title="spacing">
          {Object.entries(active.spacing).map(([k, v]) => (
            <Row key={k} label={k}><input value={String(v)} onChange={(e) => setSpacing(k, e.target.value)} style={inputStyle} /></Row>
          ))}
        </Group>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = { background: 'var(--color-input-bg)', border: '1px solid var(--color-input-border)', color: 'var(--color-text)', borderRadius: 4, padding: '2px 6px', flex: 1 };

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--color-text-muted)' }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono" style={{ width: 90 }}>{label}</span>
      {children}
    </div>
  );
}
/** <input type="color"> requires #rrggbb. The #000000 fallback is swatch-only;
 *  the adjacent text input always shows/edits the real value, so non-hex values
 *  like oklch(...) or rgb(...) still edit fine. Do not "fix" the fallback. */
function normalizeColor(v: string): string {
  return /^#[0-9a-f]{6}$/i.test(v) ? v : '#000000';
}
